import React, { useState, useEffect, useCallback, useRef } from 'react';
import SettingsService from '../../services/SettingsService';
import AppearanceSection from '../settings/AppearanceSection';
import SyncSection from '../settings/SyncSection';
import NotificationsSection from '../settings/NotificationsSection';
import AccountsSection from '../settings/AccountsSection';
import SystemSection from '../settings/SystemSection';
import IntegrationsSection from '../settings/IntegrationsSection';
import '../../styles/settings.css';

const TABS = [
  { key: 'appearance',    label: 'Appearance',    icon: 'bi-palette' },
  { key: 'sync',          label: 'Sync',          icon: 'bi-arrow-repeat' },
  { key: 'notifications', label: 'Notifications', icon: 'bi-bell' },
  { key: 'accounts',      label: 'Accounts',      icon: 'bi-people' },
  { key: 'system',        label: 'System',        icon: 'bi-gear-wide-connected' },
  { key: 'integrations',  label: 'Integrations',  icon: 'bi-plug' },
];

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('appearance');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await SettingsService.getAll();
        if (!cancelled) {
          setSettings(data.settings);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load settings');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const saveSettings = useCallback((updated) => {
    setSettings(updated);
    setSaveStatus('saving');

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        await SettingsService.updateAll(updated);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2000);
      } catch {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 3000);
      }
    }, 500);
  }, []);

  const handleChange = useCallback((key, value) => {
    setSettings(prev => {
      const updated = { ...prev, [key]: value };
      saveSettings(updated);
      return updated;
    });
  }, [saveSettings]);

  const renderSection = () => {
    if (!settings) return null;
    switch (activeTab) {
      case 'appearance':    return <AppearanceSection settings={settings} onChange={handleChange} />;
      case 'sync':          return <SyncSection settings={settings} onChange={handleChange} />;
      case 'notifications': return <NotificationsSection settings={settings} onChange={handleChange} />;
      case 'accounts':      return <AccountsSection />;
      case 'system':        return <SystemSection settings={settings} onChange={handleChange} />;
      case 'integrations': return <IntegrationsSection />;
      default:              return null;
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h2 className="settings-page-title">Settings</h2>
        <div className="settings-header-right">
          <p className="settings-page-subtitle">Configure your sync monitor dashboard.</p>
          {saveStatus && (
            <span className={`settings-save-status settings-save-status--${saveStatus}`}>
              {saveStatus === 'saving' && <><i className="bi bi-arrow-repeat spin" /> Saving...</>}
              {saveStatus === 'saved' && <><i className="bi bi-check-circle" /> Saved</>}
              {saveStatus === 'error' && <><i className="bi bi-exclamation-circle" /> Save failed</>}
            </span>
          )}
        </div>
      </div>

      <nav className="data-subnav" aria-label="Settings sections">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`data-subnav-pill${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            aria-current={activeTab === tab.key ? 'page' : undefined}
          >
            <i className={`bi ${tab.icon}`} />
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="settings-section">
        {loading && <div className="settings-loading"><i className="bi bi-arrow-repeat spin" /> Loading settings...</div>}
        {error && (
          <div className="data-error-banner">
            <i className="bi bi-exclamation-triangle" />
            {error}
            <button className="settings-dismiss-btn" onClick={() => setError(null)}>&times;</button>
          </div>
        )}
        {!loading && !error && renderSection()}
      </div>
    </div>
  );
};

export default SettingsPage;
